/**
 * A running or paused scan, pinned above both tabs with its live progress (DEC-191
 * decision 1): "Wave 3 of 4 · Architecture is running now · 9 of 13 agents done".
 * Counts while running are before the Critic's review, and the card says so.
 */
import type { LiveScanSummary } from '../../types/api'
import { RowActions, type RowAction, StatusPill, VisibilityPill } from './ScanRow'
import { durLabel, liveSentence, repoLabel, sourceLabel } from './scanFormat'
import { shortDuration } from '../../utils/runTime'
import type { ScanStatusKey } from './scanStatus'
import { useConfirm } from './useConfirm'

export function LiveScanCard({
  live,
  status,
  actions,
  note,
  now = Date.now(),
}: {
  live: LiveScanSummary
  status: ScanStatusKey
  actions: RowAction[]
  /** DEC-192: on a paused scan, when Archive and Delete permanently appear. */
  note?: string
  now?: number
}) {
  const confirm = useConfirm()
  const pct = live.agents_total ? Math.round((live.agents_done / live.agents_total) * 100) : 0
  const paused = status === 'paused'
  return (
    <div
      data-testid="live-card"
      className={`flex flex-col gap-[10px] rounded-lg border px-[18px] py-4 shadow-card ${
        paused ? 'border-warn/40 bg-panel' : 'border-accent/40 bg-gradient-to-br from-accent/[0.16] to-accent/[0.04]'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill status={status} />
        <span className="text-[14px] font-semibold text-ink">{repoLabel(live.repo_url)}</span>
        <span className="text-[12px] text-muted">{sourceLabel(live.repo_url, live.branch)}</span>
        <VisibilityPill visibility={live.visibility} />
        {live.pushed_by && <span className="text-[12px] text-muted">Pushed by @{live.pushed_by}</span>}
      </div>
      <span className="text-[14px] text-body">{liveSentence(live, status)}</span>
      {note && (
        <span data-testid="paused-offer" className="text-[12px] leading-[1.45] text-muted">
          {note}
        </span>
      )}
      <span className="block h-2 overflow-hidden rounded-full bg-fill-subtle" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <span
          className={`block h-full rounded-full ${paused ? 'bg-mark-warn' : 'bg-mark-running'}`}
          style={{ width: `${Math.max(pct, live.has_events ? 3 : 0)}%` }}
        />
      </span>
      <div className="flex flex-wrap justify-between gap-x-2 gap-y-1 text-[12px] text-muted">
        <span className="whitespace-nowrap">
          {paused
            ? `paused at ${pct}%`
            : `about ${pct}% · ${
                typeof live.run_seconds === 'number'
                  ? `running ${shortDuration(live.run_seconds)}`
                  : `running for ${durLabel(live.started_at, now)}`
              }`}
        </span>
        <span className="whitespace-nowrap">{live.findings_so_far} found so far (before review)</span>
      </div>
      <RowActions actions={actions} onAsk={confirm.ask} align="start" />
      {confirm.box}
    </div>
  )
}
